/**
 * Scenario intent shared by the developer UI, browser state and future
 * materialization adapters. Environment-specific response shapes belong to
 * those adapters.
 */

export const LIMIT_SCENARIO_OPTIONS = [
    { id: 'custom-theme', label: 'custom-theme', kind: 'flag' },
    { id: 'staff', label: 'staff', kind: 'numeric', defaultValue: 5 },
    { id: 'newsletters', label: 'newsletters', kind: 'numeric', defaultValue: 3 },
    { id: 'automations', label: 'automations', kind: 'numeric', defaultValue: 3 },
    { id: 'members', label: 'members', kind: 'numeric', defaultValue: 1200 },
    { id: 'analytics', label: 'analytics', kind: 'flag' },
    { id: 'stripe-connect', label: 'stripe-connect', kind: 'flag' },
    { id: 'subdirectory', label: 'subdirectory', kind: 'flag' },
    { id: 'sending-domain', label: 'sending-domain', kind: 'flag' },
];

const LIMIT_SCENARIOS_BY_ID = Object.fromEntries(
    LIMIT_SCENARIO_OPTIONS.map((scenario) => [scenario.id, scenario]),
);

/**
 * Display order derived from the catalog so the panel cannot drift from it.
 */
export const LIMIT_SCENARIO_IDS = LIMIT_SCENARIO_OPTIONS.map(({ id }) => id);

/**
 * Plan IDs are persistence values; labels are presentation metadata. Future
 * adapters can add synthetic plans without deriving behavior from the label.
 */
export const PLAN_OVERRIDE_OPTIONS = [
    { id: 'trial', label: 'trial' },
    { id: 'starter', label: 'starter' },
    { id: 'publisher', label: 'publisher' },
    { id: 'business', label: 'business' },
    { id: 'legacy-starter', label: 'Legacy Starter' },
    { id: 'legacy-creator', label: 'Legacy Creator' },
    { id: 'legacy-team', label: 'Legacy Team' },
    { id: 'legacy-business', label: 'Legacy Business' },
    { id: 'legacy-basic', label: 'Legacy Basic' },
];

export const PLAN_OVERRIDE_IDS = PLAN_OVERRIDE_OPTIONS.map(({ id }) => id);

/**
 * @param {string} id
 * @returns {boolean}
 */
export function isNumericScenario(id) {
    return LIMIT_SCENARIOS_BY_ID[id]?.kind === 'numeric';
}

/**
 * @param {string} id
 * @returns {boolean}
 */
export function isFlagScenario(id) {
    return LIMIT_SCENARIOS_BY_ID[id]?.kind === 'flag';
}

/**
 * @param {string} id
 * @returns {boolean}
 */
export function isKnownLimitScenario(id) {
    return id === 'none' || Boolean(LIMIT_SCENARIOS_BY_ID[id]);
}

/**
 * @param {string} id
 * @returns {number}
 */
export function getNumericScenarioDefault(id) {
    return LIMIT_SCENARIOS_BY_ID[id].defaultValue;
}

/**
 * Coerces a raw numeric-scenario value to a non-negative integer, falling
 * back to the scenario default when the value is absent or invalid.
 *
 * @param {string} id
 * @param {string|number} rawValue
 * @returns {number}
 */
export function normaliseNumericValue(id, rawValue) {
    const parsed = parseInt(rawValue, 10);
    return Number.isInteger(parsed) && parsed >= 0 ? parsed : getNumericScenarioDefault(id);
}
